import React, { useState, forwardRef, useEffect } from 'react';
import BlockUi from 'react-block-ui';
import 'react-datepicker/dist/react-datepicker.css';
import formatNumber from '../../../../utils/formatNumber.js';
import './../entregas-x-lote.css';

import { PieChart, Pie, Cell, Label } from 'recharts';

import { useCoopConfig } from '../../../../components/content/coop-config.jsx';
import CardContainer from '../../../../components/card/card-container.jsx';
import LabelEudrProducer from '../../../../components/label/label-eudr-prod.jsx';
import getRiesgoEudrLabel from '../../../../nuup/utils/getRiesgoEudrLabel.js';

const COLORS_DON = ['#FFEED9', '#BC8268'];

const LoteInfoCard = forwardRef(({ loteData: data = {}, estados = [], blocking=false }, ref) => {
  const coopConfig = useCoopConfig();
  const [dataGrafica, setDataGrafica] = useState([]);
  const [unidades, setUnidades] = useState('');
  const [volumen, setVolumen] = useState({
    total: 0,
    asignado: 0,
    por_asignar: 0,
  });

  useEffect(() => {
    if (data?.lote_kgs_totales > 0) {
      const pct = (Number(data.lote_kgs_entregados) / Number(data.lote_kgs_totales)) * 100;
      setDataGrafica([
        { name: 'Restante', value: 100 - pct },
        { name: 'Entregado', value: pct },
      ]);
    }
  }, [data]);

  useEffect(() => {
    //TODO: use abreviacion_plural? 
    if (coopConfig.acopioUnits()) {
      if (coopConfig.hasAcopioUnitsExtra()) {
      setUnidades(coopConfig.acopioUnitsExtra()?.abreviacion);
      setVolumen({
        total: data?.lote_kgs_totales_extra,
        asignado: data?.lote_kgs_entregados_extra,
        por_asignar: data?.lote_kgs_por_entregar_extra,
      });
      } else {
        setUnidades(coopConfig.acopioUnits()?.abreviacion);
        setVolumen({
          total: data?.lote_kgs_totales,
          asignado: data?.lote_kgs_entregados,
          por_asignar: data?.lote_kgs_por_entregar,
        });
      }
    }
  }, [coopConfig.config, data]);

  const estadoActual = estados.find(e => e.value == data?.id_recoleccion_acopio_estatus);

  // const unidades = coopConfig.acopioUnits()?.abreviacion_plural ?? 'kgs';
  const usaEudr = !!+coopConfig.config?.usa_eudr;

  const pct = data?.lote_kgs_totales > 0
    ? (Number(data.lote_kgs_entregados) / Number(data.lote_kgs_totales)) * 100
    : 0;

  return (
    <CardContainer height={'100%'}>
      <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
        <div className="d-flex align-items-center justify-content-center flex-wrap" style={{ gap: 5 }}>
          {/* Columna 1: Folio + Fecha en una fila, Observaciones debajo */}
          <div className="align-self-start" style={{ flex: '1 1 240px', minWidth: 200, maxWidth: 310, paddingRight: 20 }}>
            {/* Fila: Folio y Fecha */}
            <div className="d-flex align-items-center mb-3" style={{ gap: 40, justifyContent: 'space-between' }}>
              <div>
                <p className="lotes-kgs-total-label m-0">Nombre:</p>
                <span className="lotes-kgs-total-valor font-weight-bold" style={{ color: '#789D16', fontSize: 22 }}>
                  {data?.nombre ?? '-'}
                </span>
              </div>
              <div>
                <p className="lotes-kgs-total-label m-0">Folio:</p>
                <span className="lotes-kgs-total-valor font-weight-bold" style={{ color: '#789D16', fontSize: 22 }}>
                  {data?.folio ?? '-'}
                </span>
              </div>
            </div>
            <div className="mb-3">
              <p className="lotes-kgs-total-label m-0">Fecha de venta/entrega:</p>
              <span className="lotes-kgs-total-valor">{data?.fecha_entrega_venta ?? '-'}</span>
            </div>

            {/* Observaciones debajo */}
            {data?.observaciones && (
              <div>
                <p className="lotes-kgs-total-label mb-1 m-0">Observaciones</p>
                <div className="rounded p-2"
                  style={{
                    background: '#f9f6f1',
                    fontSize: 13,
                    color: '#555',
                    maxWidth: 340,
                  }}>
                  {data.observaciones}
                </div>
              </div>
            )}
          </div>

          {/* Columna 2: Estado y Riesgo EUDR */}
          <div className="d-flex flex-column align-self-start" style={{ flex: '1 0 110px', minWidth: 60, gap: 15 }}>
            <div>
              <p className="lotes-kgs-total-label mb-2 m-0">Estado</p>
              {estadoActual ? (
                <span className="d-inline-block text-white rounded font-weight-bold"
                  style={{
                    backgroundColor: estadoActual.color ?? '#f5a623',
                    padding: '3px 12px',
                    fontSize: 13,
                  }}>
                  {estadoActual.label}
                </span>
              ) : (
                <span style={{ color: '#aaa', fontSize: 13 }}>Sin estado</span>
              )}
            </div>

            {usaEudr && (
              <div>
                <p className="lotes-kgs-total-label mb-2 m-0">Riesgo EUDR</p>
                <LabelEudrProducer riesgo={getRiesgoEudrLabel(data?.riesgo_eudr)} />
              </div>
            )}
          </div>

          {/* Columna 3: Estadísticas de kgs y Gráfica */}
          <div className="d-flex" style={{ flex: '2 1 300px', gap: 5 }}>
          <div className="d-flex flex-column" style={{ minWidth: 140, gap: 8 }}>
            <div>
              <p className="lotes-kgs-total-label m-0">
                {`Volumen total`}
              </p>
              <span className="lotes-kgs-total-valor font-weight-bold" style={{ fontSize: 22 }}>
                {formatNumber(volumen?.total ?? 0)} {unidades}
              </span>
            </div>
            <div>
              <p className="lotes-kgs-total-label m-0">
                {`Volumen asignado`}
              </p>
              <span className="lotes-kgs-total-valor font-weight-bold" style={{ fontSize: 22 }}>
                {formatNumber(volumen?.asignado ?? 0)} {unidades}
              </span>
            </div>
            <div>
              <p className="lotes-kgs-total-label m-0">
                {`Volumen por asignar`}
              </p>
              <span className="lotes-kgs-total-valor font-weight-bold" style={{ fontSize: 22 }}>
                {formatNumber(volumen?.por_asignar ?? 0)} {unidades}
              </span>
            </div>
          </div>

          {/* Gráfica de pie */}
          {data?.lote_kgs_totales > 0 && (
            <div className="d-flex justify-content-center align-items-center" style={{ flex: '1 1 120px' }}>
              <PieChart width={180} height={180}>
                <Pie
                  data={dataGrafica}
                  cx={85}
                  cy={85}
                  innerRadius={58}
                  outerRadius={82}
                  paddingAngle={1}
                  dataKey="value"
                  startAngle={90}
                  endAngle={450}
                  isAnimationActive
                  >
                  {dataGrafica.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS_DON[index % COLORS_DON.length]} />
                  ))}
                  <Label
                    style={{ fontSize: 15, fontWeight: 700, fill: '#555' }}
                    value={pct.toFixed(2) + '%'}
                    position="center"
                  />
                </Pie>
              </PieChart>
            </div>
          )}
          </div>
        </div>
      </BlockUi>
    </CardContainer>
  );
});

export default LoteInfoCard;
